// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  Client,
  ClientCreateDTO,
  ClientUpdateDTO,
  PageResponse
} from '../types/client';

/**
 * Result of the brokerage-wide email availability check. When `exists=true`,
 * `relationshipId` and `clientName` identify the existing ClientProfile, and
 * the remaining fields carry that client's stored details so the Add Client /
 * Add Relationship forms can auto-fill and lock the form to reuse the existing
 * client instead of creating a duplicate. The same person can belong to many
 * relationships (one membership each), so a matching email is reused rather
 * than rejected.
 */
export type EmailAvailability = Readonly<{
  exists: boolean;
  relationshipId?: string;
  clientName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string; // ISO date (YYYY-MM-DD)
  gender?: string;
  maritalStatus?: string;
  crmPersonNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  townCity?: string;
  stateProvinceCounty?: string;
  postalCode?: string;
  countryCode?: string;
  country?: string;
}>;

export const clientApi = {
  /**
   * Get all clients in a profile (paginated)
   */
  getClients: async (profileId: string, page: number = 0, size: number = 20): Promise<PageResponse<Client>> => {
    const response = await api.get<PageResponse<Client>>(`/profiles/${profileId}/clients`, {
      params: { page, size }
    });
    return response.data;
  },

  /**
   * Get all clients for a profile (non-paginated, for LOA creation)
   */
  getClientsByProfile: async (profileId: string): Promise<Client[]> => {
    const response = await api.get<PageResponse<Client>>(`/profiles/${profileId}/clients`, {
      params: { page: 0, size: 1000 } // Large size to get all clients
    });
    return response.data.content;
  },

  /**
   * Get single client by ID
   */
  getClient: async (profileId: string, clientId: string): Promise<Client> => {
    const response = await api.get<Client>(`/profiles/${profileId}/clients/${clientId}`);
    return response.data;
  },

  /**
   * Get primary client for a profile
   */
  getPrimaryClient: async (profileId: string): Promise<Client> => {
    const response = await api.get<Client>(`/profiles/${profileId}/clients/primary`);
    return response.data;
  },

  /**
   * Create new client in profile
   */
  createClient: async (profileId: string, data: ClientCreateDTO): Promise<Client> => {
    const response = await api.post<Client>(`/profiles/${profileId}/clients`, data);
    return response.data;
  },

  /**
   * Update client
   */
  updateClient: async (profileId: string, clientId: string, data: ClientUpdateDTO): Promise<Client> => {
    const response = await api.put<Client>(`/profiles/${profileId}/clients/${clientId}`, data);
    return response.data;
  },

  /**
   * Set client as primary for the profile
   */
  setPrimaryClient: async (profileId: string, clientId: string): Promise<Client> => {
    const response = await api.patch<Client>(`/profiles/${profileId}/clients/${clientId}/set-primary`);
    return response.data;
  },

  /**
   * Delete client from profile
   */
  deleteClient: async (profileId: string, clientId: string): Promise<void> => {
    await api.delete(`/profiles/${profileId}/clients/${clientId}`);
  },

  /**
   * Search clients in a profile
   */
  searchClients: async (profileId: string, query: string, page: number = 0, size: number = 20): Promise<PageResponse<Client>> => {
    const response = await api.get<PageResponse<Client>>(`/profiles/${profileId}/clients/search`, {
      params: { query, page, size }
    });
    return response.data;
  },

  /**
   * Check whether an email is already in use by an active client in the
   * current brokerage. Drives inline onBlur validation in Add Client /
   * Add Relationship forms so duplicates are flagged before submit.
   *
   * Returns the full availability payload — when `exists=true`, the
   * `relationshipId` of the owning Profile is included so callers can
   * scope by relationship instead of just bouncing off a boolean.
   */
  checkEmailExists: async (email: string): Promise<EmailAvailability> => {
    const response = await api.get<EmailAvailability>(`/clients/check-email`, {
      params: { email },
    });
    return response.data;
  },
};
